import React from 'react'
import '../home.css';
import SEO from '../SEO.js';


const VRCooking = () => {

    const secondaryFontStyle = { fontFamily: "'Raleway', sans-serif" };

    return (
        <div>
                <SEO
                    title="VR Pancake Cooking Simulator"
                    description="A virtual reality project by Lord Crawford exploring immersive meal preparation — guiding users step-by-step through making pancakes in a full-scale virtual kitchen."
                    path="/vrcooking"
                />
                <div className="text-[4vw] ml-[3.5%] mt-[2%] space-y-[-2vw] invisible title-spacer" aria-hidden="true"><div>Lord</div><div>Crawford</div></div>
                <div className='pb-[6vw] px-[3.5%]'>
                    <div className='mt-[12%] md:mt-[6%] max-w-[860px]'>
                        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-14'>
                            <h2
                                id="es_title"
                                className="text-[5.5vw] md:text-[2.6vw] text-yellow-500 font-normal leading-[1.05]"
                            >
                                VR Pancake Cooking Simulator
                            </h2>
                            <a
                                href="https://youtu.be/qeGczrRkh34"
                                className="w-fit bg-yellow-500 px-5 py-3 text-white hover:bg-yellow-600 text-center font-raleway whitespace-nowrap md:ml-6"
                            >
                                Visit Live Demo &rarr;
                            </a>
                        </div>

                        <p className="mt-10 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                            This project explored using virtual reality to teach meal preparation, guiding users step-by-step through making pancakes. Set in a full-scale virtual kitchen, users teleport around the space and interact with utensils, appliances, and ingredients using hand tracking or controllers — creating an immersive, hands-on learning environment.
                        </p>

                        <div className="mt-8 border-t border-stone-200" />

                        <div className="mt-8">
                            <h2 className="text-[22px] md:text-[26px] font-normal text-black">Specifications</h2>
                            <div style={secondaryFontStyle} className="mt-3 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                                <p><b>Dates:</b> Jan 2022 - May 2022</p>
                                <p><b>Client(s):</b> Columbia University CS Department</p>
                                <p><b>Tools Used:</b> Unity 3D, C#, MRTK (Hand-Tracking), Oculus Quest 2</p>
                                <p><b>Role:</b> Developer on a team of 3 other developers, led Object Manipulation</p>
                                <p className="mt-2">
                                    <b>Demo Video:</b>{' '}
                                    <a href="https://youtu.be/qeGczrRkh34" style={secondaryFontStyle} className="text-yellow-600 font-semibold hover:underline font-raleway">
                                        https://youtu.be/qeGczrRkh34
                                    </a>
                                </p>
                            </div>
                        </div>

                        <div className="mt-10 border-t border-stone-200" />

                        <div className="mt-10">
                            <h2 className="text-[22px] md:text-[26px] font-normal text-black">Ideation</h2>
                            <p className="mt-3 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                                As a team, we focused on exploring virtual environments familiar to most people and
                                identifying simple tasks to perform within them. Cooking emerged as an ideal choice,
                                given its universality and the ease of visualizing a kitchen setting. We brainstormed
                                simple cooking tasks with fewer than ten steps and decided on making pancakes using a
                                pre-made mix. This approach allowed us to design a straightforward kitchen environment,
                                organize materials efficiently, and create a clear, engaging process for preparing a
                                real meal.
                            </p>
                        </div>

                        <div className="mt-10 border-t border-stone-200" />

                        <div className="mt-10">
                            <h2 className="text-[22px] md:text-[26px] font-normal text-black">Storyboard</h2>
                            <p className="mt-3 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                                The below storyboard mocks an example scenario where 'Evelyn' is using our proposed VR
                                Cooking Pancake Simulator during one of their streams.
                            </p>
                            <img className="mt-6 w-[95%] md:w-[80%] mx-auto h-auto object-cover" src={require('../photos/vrcooking/storyboard1_vrcooking.png')} alt="lo-fi wireframe"/><br/>
                        </div>

                        <div className="mt-10 border-t border-stone-200" />

                        <div className="mt-10">
                            <h2 className="text-[22px] md:text-[26px] font-normal text-black">Technical Development</h2>

                            <h3 className="mt-6 text-[18px] md:text-[20px] font-medium text-black font-raleway">Object Manipulation (My Role)</h3>
                            <p className="mt-2 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                                I built the core object interaction system — enabling users to grab, translate, and rotate objects using one-handed controller input, with user hands treated as physics objects. Beyond basic manipulation, I programmed objects to change state based on what's done to them: ingredients combine when mixed, food transforms under heat, and pouring actions trigger only when an object is rotated past a defined angle. I also developed the oven's cooking logic, handling how objects change form over time as heat is applied. These systems together formed the physical backbone of the cooking experience.
                            </p>

                            <div className="mt-6">
                                <p className="text-center text-[15px] text-stone-500 font-raleway">Interacting with ingredients</p>
                                <div className="mt-3 flex flex-col md:flex-row place-content-center gap-4">
                                    <img className="w-[95%] md:w-[45%] object-cover" src={require('../photos/vrcooking/object3.jpg')} alt="first hi-fi wireframe"/>
                                    <img className="w-[95%] md:w-[45%] object-cover" src={require('../photos/vrcooking/object2.jpg')} alt="first hi-fi wireframe"/>
                                </div>
                                <p className="mt-8 text-center text-[15px] text-stone-500 font-raleway">Cooking using the ingredients and tools</p>
                                <img className="mt-3 w-[95%] md:w-[65%] mx-auto h-auto object-cover" src={require('../photos/vrcooking/object1.jpg')} alt="first hi-fi wireframe"/>
                            </div>

                            <h3 className="mt-10 text-[18px] md:text-[20px] font-medium text-black font-raleway">Wayfinding</h3>
                            <p className="mt-2 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                                A teammate built the movement system, allowing users to navigate the kitchen via gaze-directed teleportation with wrist rotation constraints. Directional arrows guide users to the next required object (like finding butter in the fridge), supported by audio pings, highlight outlines, and smooth fade transitions to anchor movement. A second teammate developed the task board system, which drives the step-by-step cooking instructions and controls simulator state throughout.
                            </p>
                            <img className="mt-6 w-[90%] md:w-[54%] mx-auto h-auto object-cover" src={require('../photos/vrcooking/wayfinding.jpg')} alt="first hi-fi wireframe"/><br/>

                            <h3 className="mt-8 text-[18px] md:text-[20px] font-medium text-black font-raleway">Kitchen Environment</h3>
                            <p className="mt-2 text-[16px] md:text-[17px] leading-relaxed text-stone-700 font-raleway">
                                A third teammate designed and constructed the kitchen environment.
                            </p>
                            <img className="mt-6 w-[93%] md:w-[76%] mx-auto h-auto object-cover" src={require('../photos/vrcooking/kitchen.png')} alt="first hi-fi wireframe"/><br/>
                        </div>
                    </div>
                </div>
            </div>

    )
}

export default VRCooking